import { IClientDataDocBase } from '../../../services/clientData.service'
import { ITimeZoneOption } from '../../options/store/options.types'

export interface IBackupConfig {
  minIntervalMinutes: number
  retryCount: number
  disabled: boolean
}

export const DEFAULT_BACKUP_CONFIG: IBackupConfig = {
  minIntervalMinutes: 60,
  retryCount: 3,
  disabled: false,
}

export interface INotificationConfig {
  minIntervalMinutes: number
  retryCount: number
}

export const DEFAULT_NOTIFICATION_CONFIG: INotificationConfig = {
  minIntervalMinutes: 60,
  retryCount: 3,
}

interface ICmdFacProcedureDefaultAction {
  procedure: string
  action?: string | null
}

export interface IConfigurationsState {
  cadImportConfig: ICadImportConfig
  ekgImportConfig: IEkgImportConfig
  datapointsConfig: IDataPointsConfig
  serviceConfig: IServiceConfig
  servicesBillingConfig: IServicesBillingConfig
  timeZone: ITimeZoneOption | null
  isServiceConfigLoading: boolean
  cmdFacChartDefaults: ICmdFacChartDefaults
  cmdFacProcedureDefaultAction: Array<ICmdFacProcedureDefaultAction>
  commandFacilityConfig: ICommandFacilityConfig
  agencyNumberConfig: Array<IAgencyNumberConfig>
  datasetDeterminationConfig: IDatasetDeterminationConfig | null
  texasExportConfig: ITexasExportConfig | null
  texasWristbandReportTypeConfig: ITexasWristbandReportTypeConfig | null
  appSettingsEntries: IAppSettingsEntry[]
}

export interface IDataPointsConfig {
  DP_AIRWAY_FAILURE: number
  DP_BELONGINGS: number
  DP_COMPDURATION: number
  DP_DISASTER: number
  DP_FACTORS: number
  DP_INJURY: number
  DP_HIDE_FIELDS_BY_STAFF: number
  DP_PR_DISP_DELAY: number
  DP_PR_TURNAROUND_DELAY: number
  DP_MOTOR_PICKLIST: number
  DP_MOTOR_COMMENTS: number
  DP_SENSORY_PICKLIST: number
  DP_SENSORY_COMMENTS: number
  DP_PUPIL_SIZELIST: number
  DP_PUPIL_PICKLIST: number
  DP_PUPIL_COMMENTS: number
  DP_STROKE_SCALE: number
  DP_THROMBOLYTIC: number
  DP_RTS: number
  DP_RTSCALC: number
  DP_INITGCS: number
  DP_PULSE_DORS: number
  DP_TEMP: number
  DP_EDEMA: number
  DP_ARTLINE: number
  DP_SWAN: number
  DP_SHOWDEFAULTS: number
  DP_BREATH_PICKLIST: number
  DP_BREATH_COMMENTS: number
  DP_NEURO_ASSESS: number
  DP_LAST_ORAL_INTAKE: number
  DP_LEVEL_CARE_PER_PROTOCOL: number
  DP_CARDIACARREST: number
  DP_CARDIACARREST_TIMES_UNKNOWN: number
  DP_VS_GLU: number
  DP_MVI: number
  DP_OTHER_COMPLAINT: number
  DP_SECCOMPLAINT: number
  DP_SYSTEM: number
  DP_SYMPTOMS: number
  DP_IMPRESSION: number
  DP_ANALOC: number
  DP_WORKING_DIAGNOSIS: number
  DP_MASSCAS: number
  DP_TRIAGE: number
  DP_INTRATHORACIC: number
  DP_GENERAL_EXTREMITIES: number
  DP_OTHER_AGENCIES: number
  DP_CUSTOMVITALS: number
  DP_OPT1_NAME: string
  DP_OPT2_NAME: string
  DP_CREW_PS: number
  DP_FLUIDS_COLL: number
  DP_FLUIDS_OTHER: number
  DP_PG6: number
  DP_PAIN_QUALITY: number
  DP_PAIN_QUALITY_REQ: number
  DP_PAIN: number
  DP_SHOCK_INDEX: number
  DP_SEDATION: number
  DP_WITHDRAWAL: number
  DP_MAXALT: number
  DP_IFR: number
}

export interface IServiceConfig {
  DEFAULT_TEMP_MEASUREMENT: string | null
  DEFAULT_LENGTH_MEASUREMENT: string | null
  DEFAULT_WEIGHT_MEASUREMENT: string | null
  DEST_CONDITION_OR_FINAL_ACUITY: number
  DRUGALC_DEFAULT: number | null
  ENCOUNTER_DEFAULT: number | null
  EMD_DEFAULT: number | null
  FORCE_MILEAGE_TENTHS: number
  FORCE_PT_SEARCH: number
  MASSCAS_DEFAULT: number | null
  OUTCOME_DEFAULT: number | null
  PR_COMPDURATION: number
  PR_ODOM: number
  PR_LPM: number
  PR_DISPATCH_ID: number
  PR_DISPATCHED_AS: number
  PR_DISP_DELAY: number
  PR_PG_NARRATIVE: number
  PR_PMHX_LAYOUT: number
  PR_TURNAROUND_DELAY: number
  PR_PT_CRITERIA_SEARCH: number
  PR_PERTNEG: number
  REFTYPE_DEFAULT: number | null
  RECUNIT_DEFAULT: number | null
  RECTYPE_DEFAULT: number | null
  SCENE_DEFAULT: number | null
  SCHED_DEFAULT: number | null
  STAFF_DEFAULT: number | null
  TIMEZONE: number
  PR_USNG: number
  PR_NEMSIS35_DISPOSITION: number
  PR_REQ_CHART_ATTACH_CATEGORY: number
  PR_PT_MOVEMENT: number
  PR_PT_CONDITION: number
  PR_PTS_ONSCENE: number
  SHOW_REPEAT_VITALS: number
  PR_EXPOSURE: number
  DISPLAY_SECONDS: number
  PR_PTRUN_ID: number
  PR_PTRUN_ID_LABEL: string | null
  PR_PT_ID: number
  PR_PT_ID_LABEL: string | null
  PR_DISP_FIELDS: number
  PR_MODE_DESCRIPTORS: number
  PR_REFREC_MODE: number
  PR_LOCAL_ID: number
  PR_LOCAL_ID_LABEL: string | null
  PR_DISPATCHED_LOS: number
  PR_SIGNATURES: number
  USER_SECURITY_SCHEME: number
  EXTRA_SECURITY_CREW: number
  EXTRA_SECURITY_STATUS: number
  PR_TAIL_NUMBER: number
  PR_PTCAT_DISP_MAP: number
  TEAM_CAPABLE: number
  PR_DT_REC: number
  PR_DT_REC_DISABLE: number
  PR_DT_NOTIFY: number
  PR_DT_NOTIFY_DISABLE: number
  PR_DT_STANDBY: number
  PR_DT_STANDBY_DISABLE: number
  PR_DT_ACKNOWLEDGED: number
  PR_DT_ACKNOWLEDGED_DISABLE: number
  PR_DT_ENROUTE: number
  PR_DT_ENROUTE_DISABLE: number
  PR_DT_STAGING: number
  PR_DT_STAGING_DISABLE: number
  PR_DT_ARRIVE: number
  PR_DT_ARRIVE_DISABLE: number
  PR_DT_ARVREC: number
  PR_DT_ARVREC_DISABLE: number
  PR_DT_CALL_COMPLETED: number
  PR_DT_CALL_COMPLETED_DISABLE: number
  DEFAULT_TIMES_UNUSED: number
  PR_DT_CANCEL: number
  PR_DT_CANCEL_DISABLE: number
  DT_RANGE_FUTURE: number
  DT_RANGE_PAST: number
  PR_CREW_PS: number
  PR_CREW_ROLE: number
  PR_DT_LVREF: number
  PR_DT_LVREF_DISABLE: number
  CUSTOM_TIME1: number | null
  CUSTOM_TIME1_LABEL: string | null
  CUSTOM_TIME2: number | null
  CUSTOM_TIME2_LABEL: string | null
  CUSTOM_TIME3: number | null
  CUSTOM_TIME3_LABEL: string | null
  CUSTOM_TIME4: number | null
  CUSTOM_TIME4_LABEL: string | null
  PR_DT_CUSTOM_TIME1_DISABLE: number | null
  PR_DT_CUSTOM_TIME2_DISABLE: number | null
  PR_DT_CUSTOM_TIME3_DISABLE: number | null
  PR_DT_CUSTOM_TIME4_DISABLE: number | null
  SPECREP_REQ_SUBJECT: number
  SPECREP_REQ_SUP: number
  SPECREP_REQ_CAT: number
  SPECREP_REQ_NARRATIVE: number
  SPECREP_PSWPM: string | null
  PR_PG6: number
  SESS_TIMEOUT: number | null
  PR_PTRESIDENT: number | null
  PR_PTCITIZENSHIP: number | null
  AIR: number | null
}
export interface ICommandFacilityConfig {
  CMDFAC_ID: number | null
  NAME: string | null
  ADDR1: string | null
  ADDR2: string | null
  CITY: string | null
  STATE: string | null
  ZIP: string | null
  CMDFAC_ADMIN: number | null
  PHONE1: string | null
  PHONE2: string | null
  FAX1: string | null
  FAX2: string | null
  RPT_STATE: string | null
  RPT_REGION: number | null
  STATE_ID: string | null
  PR_DT_INJURY: number
  PR_DT_ATPT: number
  PR_DT_LVPT: number
  PR_DT_TXCARE: number
  PT_IMPORT: number
  PAYROLL_STARTDAY: number
  PAYROLL_PERIODLENGTH: string | null
  PAYROLL_PERIODFIRSTDAY: null
  PR_DT_FRESP: number
  PR_DT_INQTRS: number
  PR_DT_NOTIFY: number
  TIMEZONE: number | null
  CAL_DAYS: number | null
  TS_MODIFIED: string | null
  MAP_NATIONAL_DEFAULTS: number | null
  NEMSIS_COMPLIANCE_STATUS: number | null
  NEMSIS_COMPLIANCE_DATE: string | null
  EMAIL_DISCLAIMER: string | null
  REGION_VIEW_USER_DATA: number
  NEMSIS3_COMPLIANCE_STATUS: number | null
  NEMSIS3_COMPLIANCE_DATE: string | null
  PR_DT_REC: number
  PR_DT_STANDBY: number
  PR_DT_ACKNOWLEDGED: number
  PR_DT_ENROUTE: number
  PR_DT_CANCEL: number
  PR_DT_ARRIVE: number
  PR_DT_TRANS_SCENE: number
  PR_DT_LVREF: number
  PR_DT_ARVREC: number
  PR_DT_CALL_COMPLETED: number
  CUSTOM_TIME1: number | null
  CUSTOM_TIME1_LABEL: string | null
  CUSTOM_TIME2: number | null
  CUSTOM_TIME2_LABEL: string | null
  CUSTOM_TIME3: number | null
  CUSTOM_TIME3_LABEL: string | null
  CUSTOM_TIME4: number | null
  CUSTOM_TIME4_LABEL: string | null
  PR_DT_STAGING: number
}
export interface ICmdFacChartDefaults {
  PUPIL_REACT: string | null
  NEURO_SENSORY: string | null
  NEURO_MOTOR: string | null
  PUPIL_SIZE_L: number
  PUPIL_SIZE_R: number
  PUPIL_RX_L: number
  PUPIL_RX_R: number
  MOTOR_LA: number
  MOTOR_RA: number
  MOTOR_LL: number
  MOTOR_RL: number
  SENSORY_LA: number
  SENSORY_RA: number
  SENSORY_LL: number
  SENSORY_RL: number
  PULSE_BRA_L: number
  PULSE_BRA_R: number
  PULSE_CAROTID_L: number
  PULSE_CAROTID_R: number
  PULSE_DORS_L: number
  PULSE_DORS_R: number
  PULSE_FEM_L: number
  PULSE_FEM_R: number
  PULSE_RAD_L: number
  PULSE_RAD_R: number
  HEAD_CONSCIOUSNESS: number
  RESP_SOUNDS: string | null
  CV_BREATH_SOUNDS_L: number
  CV_BREATH_SOUNDS_R: number
  CV_RESP_EFFORT: number
  CV_CAP_REFILL: number
  CV_EDEMA: number
  CV_JVD: number
}

export interface IServicesBillingConfig {
  ALS_ASSESS: number
  SCT_RN_IH: number
  SCT_MEDS: number
}

export interface IAgencyNumberOverrideConfig {
  id: number
  name: string
  agencyNumber: string
}

export interface IAgencyNumber {
  dataset: string
  agencyNumber: string
}
export type IAgencyNumbers = Record<string, string | null>

export interface IAgencyNumberConfig extends IAgencyNumber {
  baseOverrides: Array<IAgencyNumberOverrideConfig> | null
  unitOverrides: Array<IAgencyNumberOverrideConfig> | null
  countyOverrides: Array<IAgencyNumberOverrideConfig> | null
  originalAgencyNumber: string
}

export interface IDataset {
  dataset: string | null
  stateId: string | null
  includeInExport: number
  isStateSupportedInNOW: number
}
export type IDatasets = Array<IDataset>

export enum DatasetDeterminationConstants {
  BY_DEFAULT_DATASET = 1,
  BY_BASE_SITE = 2,
  BY_UNIT_STATE = 3,
  BY_REF_STATE = 4,
  BY_REC_STATE = 5,
  BY_REF_AND_REC_STATE = 6,
  BY_BASE_SITE_REGION = 7,
}

export interface IDatasetDeterminationConfig {
  serviceId: number
  activateNemsis: number | null
  datasetDetermination: number
  defaultDataset: string | null
  datasets: IDatasets
}

export interface ITexasExportConfig {
  serviceId: number
  isTexasExport: number
}

export interface ITexasExportConfigResponse extends IClientDataDocBase {
  entity: ITexasExportConfig
}

export interface ITexasWristbandReportTypeConfig {
  id: number
  name: string
  nemsis3: number
}

export interface ITexasWristbandReportTypeConfigResponse extends IClientDataDocBase {
  entity: Array<ITexasWristbandReportTypeConfig>
}

export interface IServiceConfigResponse extends IClientDataDocBase {
  entity: Array<IServiceConfig>
}

export interface IServicesBillingConfigResponse extends IClientDataDocBase {
  entity: Array<IServicesBillingConfig>
}

export interface IIDataPointsConfigResponse extends IClientDataDocBase {
  entity: Array<IDataPointsConfig>
}

export interface IAgencyNumberConfigResponse extends IClientDataDocBase {
  entity: Array<IAgencyNumberConfig>
}

export interface IAppSettingsEntry {
  key: string
  value: number
}

export interface IConfigurationsAction extends IConfigurationsState {
  type: string
  dataset: string
  agencyNumber: string
}

export interface ICadImportConfig {
  filter_unit: number
}

export interface IEkgImportConfig {
  ekgAttachmentCategory: number | null
}
